import {
    Image, MergeRequest, RecipeListItem, Tool, ToolUpdate, ToolWithUsage,
} from './types';
import { authService } from '../auth/AuthService.js';

export async function getTool(toolId: string): Promise<Tool> {
    const response = await fetch(`/api/tool/${toolId}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
}

export async function createTool(tool: Omit<Tool, 'id'>): Promise<Tool> {
    const response = await fetch('/api/tool/', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...authService.getAuthHeaders(),
        },
        body: JSON.stringify(tool),
    });
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
}

export async function updateTool(toolId: string, toolUpdate: ToolUpdate): Promise<Tool> {
    const response = await fetch(`/api/tool/${toolId}`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            ...authService.getAuthHeaders(),
        },
        body: JSON.stringify(toolUpdate),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to update tool' }));
        throw new Error(error.detail || `HTTP error! status: ${response.status}`);
    }

    return response.json();
}

export async function addToolImage(toolId: string, file: Blob): Promise<Image> {
    const formData = new FormData();
    formData.append('file', file, 'image.jpg');
    const response = await fetch(`/api/tool/${toolId}/images`, {
        method: 'POST',
        headers: { ...authService.getAuthHeaders() },
        body: formData,
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to upload image' }));
        throw new Error(error.detail || `HTTP error! status: ${response.status}`);
    }
    return response.json();
}

export async function deleteToolImage(toolId: string, imageId: string): Promise<void> {
    const response = await fetch(`/api/tool/${toolId}/images/${imageId}`, {
        method: 'DELETE',
        headers: { ...authService.getAuthHeaders() },
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to delete image' }));
        throw new Error(error.detail || `HTTP error! status: ${response.status}`);
    }
}

export async function setDefaultToolImage(toolId: string, imageId: string): Promise<Tool> {
    const response = await fetch(`/api/tool/${toolId}/images/${imageId}/default`, {
        method: 'PUT',
        headers: { ...authService.getAuthHeaders() },
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to set default image' }));
        throw new Error(error.detail || `HTTP error! status: ${response.status}`);
    }
    return response.json();
}

export async function searchTools(query: string, limit: number = 20): Promise<Tool[]> {
    const params = new URLSearchParams({
        q: query,
        limit: limit.toString()
    });
    const response = await fetch(`/api/tools/search?${params}`);
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
}

/** Every tool plus its recipe-usage count — for the explorer's table. */
export async function listTools(): Promise<ToolWithUsage[]> {
    const response = await fetch('/api/tools');
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
}

export async function deleteTool(toolId: string): Promise<void> {
    const response = await fetch(`/api/tool/${toolId}`, {
        method: 'DELETE',
        headers: { ...authService.getAuthHeaders() },
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to delete tool' }));
        throw new Error(error.detail || `HTTP error! status: ${response.status}`);
    }
}

/** Merge one or more tools into mergeRequest.targetId. */
export async function mergeTools(mergeRequest: MergeRequest): Promise<Tool> {
    const response = await fetch('/api/tools/merge', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...authService.getAuthHeaders(),
        },
        body: JSON.stringify(mergeRequest),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to merge tools' }));
        throw new Error(error.detail || `HTTP error! status: ${response.status}`);
    }
    return response.json();
}

/** Recipes that contain at least one node using this tool. */
export async function getRecipesUsingTool(toolId: string): Promise<RecipeListItem[]> {
    const response = await fetch(`/api/tool/${toolId}/recipes`);
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
}
